/**
 * `/` landing — role-aware activation (N1 Item A).
 *
 * Replaces the universal hardcoded `<Navigate to="/workspace" />` with a
 * runtime consumer of the frozen role-navigation matrix
 * (`getRoleNavigationProfile` / `filterAreasForRole` via
 * `resolveRoleAwareLandingPath`). `external` entityType never reaches this
 * component — `BusinessPlaneGuard` redirects it to the Portal before
 * `MainRouter` mounts — so this only runs for admitted business-plane actors.
 *
 * Fail-closed: `resolveExperienceRoleId` returns `null` unless the JWT carries
 * an exact frozen `ExperienceRoleId`, or the grounded technical signals
 * `role === 'owner'` / `permissions` contains `'system:admin'` (→ tenant_admin).
 * A `null` role resolves to the exact same `/workspace` default that existed
 * before this activation — no privilege escalation, no invented roles.
 */
import React from 'react';
import { Navigate } from 'react-router-dom';
import { resolveRoleAwareLandingPath } from '@shared/frontend-experience-architecture-and-navigation-canon';
import { getCurrentTokenPayload } from '../utils/jwtUtils';
import { resolveExperienceRoleId } from '../utils/experienceRoleResolver';

/** Pre-activation universal default — unchanged fail-closed fallback target. */
export const UNRESOLVED_ROLE_LANDING_FALLBACK = '/workspace';

export const RoleAwareLandingRedirect: React.FC = () => {
  const payload = getCurrentTokenPayload();
  const roleId = resolveExperienceRoleId(payload);
  const target = resolveRoleAwareLandingPath(roleId, UNRESOLVED_ROLE_LANDING_FALLBACK);
  return <Navigate to={target} replace />;
};

export default RoleAwareLandingRedirect;
